import React, { useState, useEffect } from 'react';
import { ModemResponsesTable } from './ModemResponsesTable';
import { edgeDeviceService } from '../../../services/edgeDeviceService';
import { Activity, RefreshCw } from 'lucide-react';

interface ActivityTabProps {
  deviceId: string;
  refreshTrigger?: number; // Increment this to trigger a refresh
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ deviceId, refreshTrigger }) => {
  const [responses, setResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30s default

  const loadResponses = async () => {
    try {
      setLoading(true);
      const modemResponses = await edgeDeviceService.getModemResponses(deviceId, 100);
      setResponses(modemResponses);
    } catch (error) {
      console.error('Error loading modem responses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResponses();
  }, [deviceId, refreshTrigger]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-accent-light-blue dark:text-accent-blue" />
              <h3 className="text-lg font-semibold text-foreground">Command Activity & Responses</h3>
            </div>
            <p className="text-sm text-muted-foreground/70">
              Real-time AT command responses from modems. Send commands using the Quick Command panel above.
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Auto-refresh Toggle */}
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-border text-accent-blue focus:ring-accent-blue"
              />
              <span>Auto-refresh</span>
            </label>

            {/* Refresh Interval Selector */}
            {autoRefresh && (
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="px-3 py-1 text-sm border border-border rounded focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              >
                <option value={2000}>2s</option>
                <option value={5000}>5s</option>
                <option value={10000}>10s</option>
                <option value={30000}>30s</option>
              </select>
            )}

            {/* Manual Refresh Button */}
            <button
              onClick={loadResponses}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-background border border-border rounded hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Info Alert */}
        <div className="bg-accent-blue-faded border border-border rounded p-3 text-sm text-accent-blue">
          <p>
            <strong>Tip:</strong> Use the Quick Command panel above to send AT commands. 
            Responses will appear here automatically. Showing the most recent 100 responses.
          </p>
        </div>
      </div>

      {/* Responses Table */}
      <ModemResponsesTable
        deviceId={deviceId}
        responses={responses}
        loading={loading}
        onRefresh={loadResponses}
        autoRefresh={autoRefresh}
        refreshInterval={refreshInterval}
      />
    </div>
  );
};

